import React, { useState } from 'react';
import { ShieldAlert, X } from 'lucide-react';

interface WabaLockedBannerProps {
  accountName?: string | null;
  phoneNumber?: string | null;
  onDismiss?: () => void;
}

const META_SECURITY_CENTER_URL =
  'https://business.facebook.com/security_center';
const META_WA_ACCOUNT_QUALITY_URL =
  'https://business.facebook.com/wa/manage/insights';

export const WabaLockedBanner: React.FC<WabaLockedBannerProps> = ({
  accountName,
  phoneNumber,
  onDismiss,
}) => {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const label =
    accountName && phoneNumber
      ? `${accountName} (${phoneNumber})`
      : accountName || phoneNumber || 'Your WhatsApp account';

  return (
    <div
      className="z-50 flex w-full items-center justify-between gap-3 border-b border-[#FECDCA] bg-[#FEF3F2] px-4 py-2.5"
      data-testid="waba-locked-banner"
      role="status"
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#FEE4E2] text-[#B42318]">
          <ShieldAlert className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
        </span>
        <p className="min-w-0 truncate text-[13px] font-medium leading-5 text-[#912018]">
          <span className="font-semibold">{label}</span>
          {' '}
          is locked by Meta. Sending is paused until your business is verified.
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
        <a
          href={META_SECURITY_CENTER_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="waba-locked-banner-verify-cta"
          className="inline-flex h-8 items-center rounded-lg bg-[#B42318] px-3 text-[12px] font-semibold text-white transition hover:bg-[#912018] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F04438]/40"
        >
          Verify business
        </a>
        <a
          href={META_WA_ACCOUNT_QUALITY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden h-8 items-center rounded-lg px-2.5 text-[12px] font-semibold text-[#B42318] transition hover:bg-[#FEE4E2] sm:inline-flex"
          data-testid="waba-locked-banner-learn-more"
        >
          Account quality
        </a>
        <button
          type="button"
          onClick={() => {
            setDismissed(true);
            onDismiss?.();
          }}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#B42318] transition hover:bg-[#FEE4E2]"
          aria-label="Dismiss banner"
          data-testid="waba-locked-banner-dismiss"
        >
          <X className="h-4 w-4" strokeWidth={2.25} />
        </button>
      </div>
    </div>
  );
};
